/**
 * Turning data into the words a person reads.
 *
 * These live together because consistency matters more than proximity: a file
 * size must look the same in the tree, the detail panel and the download
 * dialog, or the product feels assembled from parts.
 */

/**
 * A human file size.
 *
 * Uses decimal units, because that is what the operating system's file
 * browser shows the engineers and disagreeing with it just causes doubt.
 * formatBytes : Number -> String
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes === 0) return '0 B'
  if (bytes < 1000) return `${bytes} B`
  const units = ['kB', 'MB', 'GB', 'TB']
  let value = bytes / 1000
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) { value /= 1000; unit++ }
  // One decimal below 10 ("1.4 MB"), none above ("847 kB") — enough precision
  // to compare, not so much that it becomes noise in a dense list.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * How long ago something happened, in the shortest honest form.
 * formatRelative : String Number -> String
 */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return '—'
  const ago = now - then
  if (ago < 45_000) return 'just now'
  if (ago < HOUR) return `${Math.round(ago / MINUTE)}m ago`
  if (ago < DAY) return `${Math.round(ago / HOUR)}h ago`
  if (ago < 7 * DAY) return `${Math.round(ago / DAY)}d ago`
  if (ago < 365 * DAY) {
    const weeks = Math.round(ago / (7 * DAY))
    return weeks < 9 ? `${weeks}w ago` : `${Math.round(ago / (30 * DAY))}mo ago`
  }
  return `${Math.round(ago / (365 * DAY))}y ago`
}

/** The full date and time, for a tooltip where precision matters. */
export function formatExact(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/** A date alone, e.g. for a grid tooltip. */
export function formatDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

/** "1 file" / "4 files". pluralise : Number String String -> String */
export const pluralise = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`

/**
 * Shorten a long path for a cramped row, keeping both ends.
 * "Cathode_Cup_Assembly/CNTF/Drawing/x.PDF" -> "Cathode_Cup_Assembly/…/Drawing"
 * ellipsisPath : String Number -> String
 */
export function ellipsisPath(path: string, maxSegments = 3): string {
  const segs = path.split('/')
  if (segs.length <= maxSegments) return path
  return [segs[0], '…', ...segs.slice(-(maxSegments - 1))].join('/')
}
